/**
 * Utility per la navigazione e menu dell'applicazione
 * Centralizza la logica di routing per garantire consistenza tra desktop e mobile
 */

export interface NavItem {
  icon: string;
  label: string;
  path: string;
}

// Route permesse per ogni ruolo (da App.tsx)
export const ROLE_ROUTES = {
  admin: [
    '/cruscotto', '/admin', '/admin/documents', '/admin/audit-log', '/applications', '/valuta-inquilini',
    '/profile', '/documents', '/consents', '/notifiche', '/messaggi', '/proprieta', '/proprieta/nuova',
    '/candidature', '/recensioni', '/contracts', '/assistenza'
  ],
  tenant: [
    '/cruscotto', '/search', '/profile', '/documents', '/consents',
    '/notifiche', '/messaggi', '/candidature', '/recensioni', '/contracts', '/assistenza'
  ],
  landlord: [
    '/cruscotto', '/profile', '/documents', '/consents', '/notifiche', '/messaggi',
    '/proprieta', '/proprieta/nuova', '/candidature', '/recensioni', '/contracts', '/assistenza'
  ],
  agency: [
    '/cruscotto', '/tenant-search', '/profile', '/documents', '/consents',
    '/notifiche', '/messaggi', '/proprieta', '/proprieta/nuova', '/candidature', '/recensioni', '/contracts', '/assistenza'
  ]
} as const;

// Mappatura route -> elementi di menu
const ROUTE_NAV_MAPPING: Record<string, Omit<NavItem, 'path'>> = {
  '/cruscotto': { icon: 'dashboard', label: 'Cruscotto' },
  '/admin': { icon: 'admin', label: 'Admin' },
  '/admin/audit-log': { icon: 'audit', label: 'Audit Log' },
  '/search': { icon: 'search', label: 'Cerca' },
  '/candidature': { icon: 'docs', label: 'Candidature' },
  '/recensioni': { icon: 'review', label: 'Recensioni' },
  '/proprieta': { icon: 'landlord', label: 'Immobili' },
  '/tenant-search': { icon: 'tenant', label: 'Inquilini' },
  '/profile': { icon: 'user', label: 'Profilo' },
  '/contracts': { icon: 'docs', label: 'Contratti' },
  '/documents': { icon: 'docs', label: 'Documenti' },
  '/consents': { icon: 'settings', label: 'Consensi' },
  '/notifiche': { icon: 'alert', label: 'Notifiche' },
  '/messaggi': { icon: 'message', label: 'Messaggi' },
  '/assistenza': { icon: 'help', label: 'Assistenza' }
};

/**
 * Ottiene gli elementi di navigazione per un ruolo specifico (o array di ruoli)
 * Filtra le route permesse e le converte in elementi di menu
 * Se vengono passati ruoli multipli, combina le route permesse
 */
export function getNavItemsForRole(role: string | string[] | undefined): NavItem[] {
  if (!role) return [];

  // Gestisci ruoli multipli
  const roles = Array.isArray(role) ? role : [role];
  const allowedRoutes = new Set<string>();

  roles.forEach(r => {
    if (ROLE_ROUTES[r as keyof typeof ROLE_ROUTES]) {
      const routes = ROLE_ROUTES[r as keyof typeof ROLE_ROUTES];
      routes.forEach(route => allowedRoutes.add(route));
    }
  });

  // Filtra solo le route che hanno una mappatura nel menu
  const menuRoutes = Array.from(allowedRoutes).filter(route => ROUTE_NAV_MAPPING[route]);

  // Converte le route in elementi di menu
  return menuRoutes.map(route => ({
    ...ROUTE_NAV_MAPPING[route],
    path: route
  }));
}

/**
 * Ottiene gli elementi di navigazione esclusi dalla navigazione mobile principale
 * Utile per i menu a tendina che non devono duplicare gli elementi già visibili
 * Per utenti con ruoli multipli, usa la logica del ruolo primario (landlord se presente, altrimenti tenant)
 */
export function getNavItemsExcludingMobileMain(role: string | string[] | undefined): NavItem[] {
  const allItems = getNavItemsForRole(role);

  if (!role) return allItems;

  // Determina il ruolo primario per la navigazione mobile
  const roles = Array.isArray(role) ? role : [role];
  const primaryRole = roles.includes('landlord') ? 'landlord' : 
                     roles.includes('tenant') ? 'tenant' : 
                     roles[0];

  // Elementi da escludere dalla navigazione mobile principale
  const excludedPaths = primaryRole === 'tenant'
    ? ['/notifiche', '/candidature', '/search', '/cruscotto']
    : primaryRole === 'landlord'
    ? ['/notifiche', '/candidature', '/tenant-search', '/cruscotto']
    : [];

  return allItems.filter(item => !excludedPaths.includes(item.path));
}

/**
 * Ottiene gli elementi di navigazione per l'header desktop (principali)
 * Logica flessibile che può adattarsi alle dimensioni schermo o zoom
 */
export function getNavItemsForDesktopHeader(role: string | undefined): NavItem[] {
  const allItems = getNavItemsForRole(role);

  if (!role) return [];

  // Logica flessibile: determina quanti elementi mostrare nell'header
  // basandosi su dimensioni schermo, zoom, o altre condizioni
  const getHeaderItemCount = (): number => {
    // Aumentato a 6 per includere contratti
    return 6;
  };

  const maxHeaderItems = getHeaderItemCount();

  // Priorità degli elementi per l'header (da più a meno importante)
  const priorityOrder = [
    '/cruscotto',      // Sempre primo
    '/search',         // Ricerca per tenant
    '/tenant-search',  // Ricerca per landlord
    '/candidature',    // Sempre importante
    '/contracts',      // Aggiunto: contratti
    '/recensioni',     // Recensioni
    '/notifiche',      // Notifiche
    // Altri elementi verranno aggiunti automaticamente
  ];

  // Crea lista ordinata per priorità
  const prioritizedItems: NavItem[] = [];
  const remainingItems: NavItem[] = [];

  priorityOrder.forEach(path => {
    const item = allItems.find(i => i.path === path);
    if (item) {
      prioritizedItems.push(item);
    }
  });

  // Aggiungi eventuali elementi non nella lista priorità
  allItems.forEach(item => {
    if (!prioritizedItems.find(p => p.path === item.path)) {
      remainingItems.push(item);
    }
  });

  // Combina e limita al numero massimo
  const headerItems = [...prioritizedItems, ...remainingItems].slice(0, maxHeaderItems);

  return headerItems;
}

/**
 * Ottiene gli elementi di navigazione per il dropdown dell'header desktop
 * Esclude automaticamente gli elementi già presenti nell'header
 */
export function getNavItemsForDesktopDropdown(role: string | undefined): NavItem[] {
  const allItems = getNavItemsForRole(role);
  const headerItems = getNavItemsForDesktopHeader(role);

  // Escludi gli elementi già presenti nell'header
  const headerPaths = headerItems.map(item => item.path);

  return allItems.filter(item => !headerPaths.includes(item.path));
}

/**
 * Verifica se un ruolo (o array di ruoli) può accedere a una route specifica
 */
export function canAccessRoute(role: string | string[] | undefined, path: string): boolean {
  if (!role) return false;

  const roles = Array.isArray(role) ? role : [role];

  return roles.some(r => {
    if (!ROLE_ROUTES[r as keyof typeof ROLE_ROUTES]) {
      return false;
    }

    const allowedRoutes = ROLE_ROUTES[r as keyof typeof ROLE_ROUTES] as readonly string[];
    return allowedRoutes.includes(path);
  });
}